import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import {
  concatMap,
  catchError,
  map,
  mergeMap,
  switchMap,
} from 'rxjs/operators';

import { TrialAccountsService } from '../services/trial-accounts.service';
import { TrialAccountRequestActions } from './actions';

@Injectable()
export class TrialAccountsRequestsEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly trialService: TrialAccountsService,
    private readonly toastrService: ToastrService,
  ) {}

  loadRequests$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TrialAccountRequestActions.GetItems.request),
      switchMap(({ httpFilters }) =>
        this.trialService.getTrialAccountRequests(httpFilters).pipe(
          map(({ items }) =>
            TrialAccountRequestActions.GetItems.success({ items, httpFilters }),
          ),
          catchError((err: HttpErrorResponse) => {
            this.toastrService.error(err?.message);
            return [
              TrialAccountRequestActions.GetItems.failed(),
              TrialAccountRequestActions.failure({
                errorMessage: err?.message,
              }),
            ];
          }),
        ),
      ),
    ),
  );

  approveRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TrialAccountRequestActions.RequestApprove.request),
      concatMap(({ requestId, accountId, redirect }) =>
        this.trialService.approveTrialRequest(accountId, requestId).pipe(
          map((request) => {
            this.toastrService.success(
              'Trial account has successfully been approved',
              'Trial Account Approve',
            );
            if (redirect) {
              this.router.navigate([`/au/accounts/${accountId}`]);
            }
            return TrialAccountRequestActions.RequestApprove.success({
              requestId: request.id,
            });
          }),
          catchError((err) => {
            this.toastrService.error(err?.message);
            return of(
              TrialAccountRequestActions.failure({
                errorMessage: err?.message,
              }),
            );
          }),
        ),
      ),
    ),
  );

  declineRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TrialAccountRequestActions.RequestDecline.request),
      mergeMap(({ requestId }) =>
        this.trialService.declineTrialRequest(requestId).pipe(
          map((request) =>
            TrialAccountRequestActions.RequestDecline.success({
              requestId: request.id,
            }),
          ),
          catchError((err) => {
            this.toastrService.error(err?.message, 'Trial Account Decline');
            return of(
              TrialAccountRequestActions.failure({
                errorMessage: err?.message,
              }),
            );
          }),
        ),
      ),
    ),
  );
}
